import React, { useEffect, useState, useMemo } from 'react';
import { getAdminRoles, toggleRoleStatus } from '../../services/adminApi';
import ReusableAdminTable from './ReusableAdminTable';
import { useNavigate } from 'react-router-dom';
import { deleteRoleApi } from '../../services/api';

// UI & Icons
import { Button, Badge } from 'react-bootstrap';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import Swal from 'sweetalert2';

const AdminRoleList = () => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const loadRoles = async () => {
        setIsLoading(true);
        try {
            const res = await getAdminRoles();
            setRoles(res.data);
        } catch (error) {
            console.error("Error fetching roles", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    const handleEdit = (localId) => {
        navigate(`/admin/post-roles/${localId}`);
    };

    const handleDelete = async (localId, roleName) => {
        const result = await Swal.fire({
            title: 'Delete Role?',
            text: `Are you sure you want to delete "${roleName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#c30000',
            cancelButtonColor: '#374151',
            background: '#111827',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            await deleteRoleApi(localId);

            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Role deleted successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadRoles();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: 'Failed to delete role.',
                background: '#111827',
                color: '#fff'
            });
        }
    };

    const handleAddNew = () => {
        navigate('/admin/post-roles');
    };

    const handleToggleStatus = async (id, role) => {
        try {
            const isActive = role.status === 'active';

            const result = await Swal.fire({
                title: isActive ? 'Deactivate Role?' : 'Activate Role?',
                text: isActive ? 'This role will become inactive.' : 'This role will become active.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: isActive ? 'Yes, Deactivate' : 'Yes, Activate',
                cancelButtonText: 'Cancel',
                confirmButtonColor: isActive ? '#c30000' : '#16a34a',
                cancelButtonColor: '#374151',
                background: '#111827',
                color: '#fff'
            });

            if (!result.isConfirmed) return;

            const res = await toggleRoleStatus(id);

            if (res.data.block) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Action Blocked',
                    text: res.data.message,
                    background: '#111827',
                    color: '#fff'
                });
                return;
            }

            await Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: isActive ? 'Role deactivated successfully.' : 'Role activated successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadRoles();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Something Went Wrong',
                text: 'Failed to update role status.',
                background: '#111827',
                color: '#fff'
            });
        }
    };

    const columns = useMemo(() => [
        { accessorKey: 'id', header: 'ID' },
        { accessorKey: 'role_name', header: 'Role Name' },
        { accessorKey: 'project.project_name', header: 'Project' },
        { accessorKey: 'required_gender', header: 'Gender' },
        { accessorKey: 'required_age', header: 'Age Range' },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: info => (
                <Badge bg={info.getValue() === 'Open' ? 'success' : 'danger'}>{info.getValue()}</Badge>
            )
        },
        {
            id: 'toggle',
            header: 'Control',
            cell: (info) => {
                const status = info.row.original.status;
                return (
                    <Button
                        size="sm"
                        variant={status === 'active' ? 'success' : 'secondary'}
                        onClick={() => handleToggleStatus(info.row.original.id, info.row.original)}
                    >
                        {status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                );
            }
        },
        {
            id: 'actions',
            header: () => <div style={{ textAlign: 'center', width: '100%' }}>ACTIONS</div>,
            cell: (info) => {
                const role = info.row.original;
                return (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        gap: '8px', 
                        width: '100%', 
                        margin: '0 auto' 
                    }}>
                        {/* ✅ EDIT BUTTON: BLUE */}
                        <button
                            type="button"
                            style={{
                                background: '#2563eb',
                                backgroundColor: '#2563eb',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                width: '32px',
                                height: '32px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                flexShrink: 0
                            }}
                            onClick={() => handleEdit(role.id)}
                            title="Edit Role"
                        >
                            <FiEdit size={15} color="#ffffff" />
                        </button>

                        {/* ✅ DELETE BUTTON: RED */}
                        <button
                            type="button"
                            style={{
                                background: '#dc2626',
                                backgroundColor: '#dc2626',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                width: '32px',
                                height: '32px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                flexShrink: 0
                            }}
                            onClick={() => handleDelete(role.id, role.role_name)}
                            title="Delete Role"
                        >
                            <FiTrash2 size={15} color="#ffffff" />
                        </button>
                    </div>
                );
            },
        }
    ], []);

    return (
        <ReusableAdminTable
            data={roles}
            columns={columns}
            isLoading={isLoading}
            title="Role Management"
            onAddNew={handleAddNew}
            addNewButtonText="Add Role"
            searchPlaceholder="Search roles..."
            exportCsvFilename="roles.csv"
        />
    );
};

export default AdminRoleList;